import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ValidationError } from 'yup';

import {
	Button,
	DefaultOptionsInterface,
	GroupSelectOptionsInterface,
	GroupSelectSitesCells,
	RadioButton,
	Select,
	SpinnerOverlay,
} from '@components/common';
import GroupSelectKpi from '@components/common/GroupSelectKpi';
import TreeGroupSelectWrapper from '@components/Kpi/Parameters/ParametersKpiSelector/TreeGroupSelectWrapper/TreeGroupSelectWrapper';
import { ETabsIds } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/tabsIds';

import { useKPI } from '@hooks/kpis/useKPI';
import { useQueryKpiGroup } from '@hooks/kpis/useQueryKpiGroup';
import { useQueryKpiSettings } from '@hooks/kpis/useQueryKpiSettings';
import { useQuerySiteVendors } from '@hooks/useQuerySiteVendors';
import { useTechnologies } from '@hooks/useTechnologies';

import { get5GLabel, isEmpty } from '@utils/common';
import { prepareKpiIdsData } from '@components/Kpi/Parameters/ParametersKpiSelector/TreeGroupSelectWrapper/TreeGroupSelect/utils';
import {
	changeOptionsByVendor,
	changeOptionsByVendorKpi,
	generateDefaultValues,
} from '@components/Kpi/Parameters/utils';
import {
	mergeKpiOptionsWithChosenValues,
	mergeOptionsWithChosenValues,
} from '@components/routes/AdminPanel/KpiAlertsManagement/utils';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IAddKpiAlertCheckForProps, IKpiAlertSchema } from '../interfaces';
import {
	IGroupSelectOptionsKpi,
} from '@components/common/GroupSelectKpi/GroupSelect.types';

import { ETechnologiesLabels, ETechnologiesValues, EVendorsValues } from '@enums/common';
import { EAggregationTypes, EFormFields } from '@enums/kpi-api';
import {
	EApplyFor,
	EConditionFields,
	EThresholdSource,
} from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/AddKpiAlertCondition/enums';

import { SEARCH_BY_SITE_OR_CELL_ID_PLACEHOLDER } from '@components/Kpi/Parameters/constants';

import { filterValuesByKeys } from '../utils';

import { ECheckForFields, TCheckForFields } from './enum';
import { checkForSchema } from './schema';

import '@components/Kpi/Parameters/Parameters.scss';

function AddKpiAlertCheckFor({
	formMethods,
	formState,
	isEdit,
	negativeBtnText,
	negativeHandler,
	positiveBtnText,
	positiveHandler,
	setActiveTab,
	values,
}: IAddKpiAlertCheckForProps) {
	const {
		clearErrors,
		control,
		handleSubmit,
		register,
		setError,
		setValue,
		watch,
	} = formMethods;

	const { kpi } = watch();

	const { errors } = formState;

	const { isLoading: isLoadingVendors, vendorsOptions } = useQuerySiteVendors();
	const { isLoading: isLoadingTechnologies } = useTechnologies();

	const [defaultValueT_4G_FDD, setDefaultValueT_4G_FDD]
		= useState<Array<DefaultOptionsInterface>>([]);
	const [defaultValueT_4G_TDD, setDefaultValueT_4G_TDD]
		= useState<Array<DefaultOptionsInterface>>([]);
	const [defaultValueT_5G, setDefaultValueT_5G]
		= useState<Array<DefaultOptionsInterface>>([]);

	useEffect(() => {
		if (values.sitesT_4G_FDD) {
			setDefaultValueT_4G_FDD(generateDefaultValues(values[ECheckForFields.sitesT_4G_FDD]!));
		}

		if (values.sitesT_4G_TDD) {
			setDefaultValueT_4G_TDD(generateDefaultValues(values[ECheckForFields.sitesT_4G_TDD]!));
		}

		if (values.sitesT_5G) {
			setDefaultValueT_5G(generateDefaultValues(values[ECheckForFields.sitesT_5G]!));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isTechnologiesDirty: boolean = (
		!!formState.dirtyFields.sitesT_4G_FDD
		|| !!formState.dirtyFields.sitesT_4G_TDD ||
		!!formState.dirtyFields.sitesT_5G
	);
	const isAllTechsEmpty: boolean = isEmpty(values.sitesT_4G_FDD)
		&& isEmpty(values.sitesT_4G_TDD)
		&& isEmpty(values.sitesT_5G);

	useEffect(() => {
		if (isAllTechsEmpty && isTechnologiesDirty) {
			setValue(ECheckForFields.kpi, null, { shouldValidate: true, shouldDirty: true });
		}
	}, [
		isAllTechsEmpty,
		isTechnologiesDirty,
		setValue,
	]);

	const vendor = watch(ECheckForFields.vendor);

	const {
		options4GFddLimenet,
		options4GFddNokia,
		options4GTddLimenet,
		options4GTddNokia,
		options5GLimenet,
		options5GNokia,
		optionsLimenetKpiLineType,
		optionsNokiaKpiLineType,
		optionsVendorsKpi,
		options5GOran,
	} = useKPI();

	const { maxSelection } = useQueryKpiSettings(vendor?.value);

	const aggregationType = watch(ECheckForFields.aggregationType);

	const isOranVendor = vendor?.value === EVendorsValues.oran;

	const filteredValuesByKeys: TCheckForFields = filterValuesByKeys(values, Object.keys(ECheckForFields));

	const { kpiGroupData } = useQueryKpiGroup({
		vendor: vendor?.value,
		isWithMaxSelection: !!maxSelection,
		isLineType: true,
	});

	const [treeKpi, setTreeKpi] = useState<IFlatMoTreeNode[]>(kpiGroupData);

	useEffect(() => {
		if (kpiGroupData.length && !treeKpi.length) {
			setTreeKpi(kpiGroupData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kpiGroupData.length]);

	useEffect(() => {
		if (values?.kpi?.length) {
			setTreeKpi(values.kpi as any);
		}
	}, [values.kpi, isEdit]);

	const setTreeKpiIds = useCallback((ids: string[]) => setValue(EFormFields.treeKpiIds, ids), [setValue]);

	const setInitKpiData = useCallback((nodes: IFlatMoTreeNode[]) => {
		setValue(ECheckForFields.kpi, nodes);
	}, [setValue]);

	const handleUpdateTreeKpi = useCallback((data: IFlatMoTreeNode[]) => {
		setTreeKpi(data);
		setTreeKpiIds(prepareKpiIdsData(data));
		setInitKpiData(data);
		clearErrors(ECheckForFields.kpi);
	}, [clearErrors, setInitKpiData, setTreeKpiIds]);

	const onHandleNext = useCallback(async () => {
		if (isEdit) {
			return handleSubmit(positiveHandler)();
		}

		try {
			await checkForSchema.validate(filteredValuesByKeys, { abortEarly: false });

			setActiveTab(ETabsIds.condition);
		} catch (err) {
			const { message, path, inner } = err as ValidationError;

			if (path) {
				setError(path, { message: message as string });
			} else {
				inner.forEach((item: any) => {
					setError(item.path, { message: item.message });
				});
			}
		}
	}, [
		filteredValuesByKeys,
		positiveHandler,
		handleSubmit,
		isEdit,
		setActiveTab,
		setError,
	]);

	const dropConditionFieldsByDefaultValues = useCallback(() => {
		setValue(EConditionFields.thresholds, null, { shouldValidate: false, shouldDirty: false });
		setValue(
			EConditionFields.thresholdSource,
			EThresholdSource.setManually,
			{ shouldValidate: false, shouldDirty: false });
		setValue(EConditionFields.applyFor, EApplyFor.allBuiltCharts, { shouldValidate: false, shouldDirty: false });
		setValue(EConditionFields.checkEveryHours, null, { shouldValidate: false, shouldDirty: false });
		setValue(EConditionFields.checkEveryMinutes, null, { shouldValidate: false, shouldDirty: false });
		setValue(EConditionFields.evaluationWindowHours, null, { shouldValidate: false, shouldDirty: false });
		setValue(EConditionFields.evaluationWindowMinutes, null, { shouldValidate: false, shouldDirty: false });
	}, [setValue]);

	const handleChangeVendor = useCallback((vendor: GroupSelectOptionsInterface): void => {
		setValue(ECheckForFields.vendor, vendor, { shouldValidate: true, shouldDirty: true });
		setValue(ECheckForFields.sitesT_5G, null, { shouldValidate: true, shouldDirty: true });
		setValue(ECheckForFields.sitesT_4G_FDD, null, { shouldValidate: true, shouldDirty: true });
		setValue(ECheckForFields.sitesT_4G_FDD, null, { shouldValidate: true, shouldDirty: true });

		if (values[EConditionFields.thresholds]) {
			dropConditionFieldsByDefaultValues();
		}
	}, [dropConditionFieldsByDefaultValues, setValue, values]);

	const clearTechErrors = useCallback(() => {
		clearErrors(ECheckForFields.sitesT_4G_FDD);
		clearErrors(ECheckForFields.sitesT_4G_TDD);
		clearErrors(ECheckForFields.sitesT_5G);
	}, [clearErrors]);

	const onChangeSitesT_4G_FDD = useCallback((sites: Array<GroupSelectOptionsInterface>): void => {
		if (!isEmpty(sites)) {
			clearTechErrors();
		}

		setValue(
			ECheckForFields.sitesT_4G_FDD,
			isEmpty(sites) ? null : sites,
			{ shouldValidate: true },
		);

		if (values[EConditionFields.thresholds]) {
			dropConditionFieldsByDefaultValues();
		}

		setDefaultValueT_4G_FDD(generateDefaultValues(sites));
	}, [clearTechErrors, dropConditionFieldsByDefaultValues, setValue, values]);

	const onChangeSitesT_4G_TDD = useCallback((sites: Array<GroupSelectOptionsInterface>): void => {
		if (!isEmpty(sites)) {
			clearTechErrors();
		}

		setValue(
			ECheckForFields.sitesT_4G_TDD,
			isEmpty(sites) ? null : sites,
			{ shouldValidate: true },
		);

		if (values[EConditionFields.thresholds]) {
			dropConditionFieldsByDefaultValues();
		}

		setDefaultValueT_4G_TDD(generateDefaultValues(sites));
	}, [clearTechErrors, dropConditionFieldsByDefaultValues, setValue, values]);

	const onChangeSitesT_5G = useCallback((sites: Array<GroupSelectOptionsInterface>): void => {
		if (!isEmpty(sites)) {
			clearTechErrors();
		}

		setValue(
			ECheckForFields.sitesT_5G,
			isEmpty(sites) ? null : sites,
			{ shouldValidate: true },
		);

		if (values[EConditionFields.thresholds]) {
			dropConditionFieldsByDefaultValues();
		}

		setDefaultValueT_5G(generateDefaultValues(sites));
	}, [clearTechErrors, dropConditionFieldsByDefaultValues, setValue, values]);

	const options5G: GroupSelectOptionsInterface[] | undefined = useMemo(() => {
		return changeOptionsByVendor({
			vendor,
			optionsLimenet: mergeOptionsWithChosenValues(options5GLimenet, values?.sitesT_5G),
			optionsNokia: mergeOptionsWithChosenValues(options5GNokia, values?.sitesT_5G),
			technology: ETechnologiesValues.T_5G,
			options5GOran,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, options5GLimenet.length, options5GNokia.length]);

	const options4GFdd: GroupSelectOptionsInterface[] | undefined = useMemo(() => {
		return changeOptionsByVendor({
			vendor,
			optionsLimenet: mergeOptionsWithChosenValues(options4GFddLimenet, values.sitesT_4G_FDD),
			optionsNokia: mergeOptionsWithChosenValues(options4GFddNokia, values.sitesT_4G_FDD),
			technology: ETechnologiesValues.T_4G_FDD,
			options5GOran,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, options4GFddLimenet.length, options4GFddNokia.length]);

	const options4GTdd: GroupSelectOptionsInterface[] | undefined = useMemo(() => {
		return changeOptionsByVendor({
			vendor,
			optionsLimenet: mergeOptionsWithChosenValues(options4GTddLimenet, values.sitesT_4G_TDD),
			optionsNokia: mergeOptionsWithChosenValues(options4GTddNokia, values.sitesT_4G_TDD),
			technology: ETechnologiesValues.T_4G_TDD,
			options5GOran,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, options4GTddLimenet.length, options4GTddNokia.length]);

	const onChangeKpi = useCallback((kpi: Array<IGroupSelectOptionsKpi>) => {
		setValue(
			ECheckForFields.kpi,
			isEmpty(kpi) ? null : kpi,
			{ shouldValidate: true, shouldDirty: true },
		);

		if (values[EConditionFields.thresholds]) {
			dropConditionFieldsByDefaultValues();
		}
	}, [dropConditionFieldsByDefaultValues, setValue, values]);

	const optionsKpi: IGroupSelectOptionsKpi[] = useMemo(() => {
		const optionsLimenet = mergeKpiOptionsWithChosenValues(optionsLimenetKpiLineType, values.kpi);
		const optionsNokia = mergeKpiOptionsWithChosenValues(optionsNokiaKpiLineType, values.kpi);

		return changeOptionsByVendorKpi({
			optionsLimenet,
			optionsNokia,
			vendor,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, optionsLimenetKpiLineType.length, optionsNokiaKpiLineType.length]);

	useEffect(() => {
		if (!optionsVendorsKpi.length) return;

		if (!vendor) {
			setValue(ECheckForFields.vendor, optionsVendorsKpi.find((vendor: any) => vendor.isDefault));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [optionsVendorsKpi?.length]);

	const onBackHandler = useCallback(() => {
		negativeHandler(ETabsIds.basicInformation);
	}, [negativeHandler]);

	const isKpiDisabled = isEmpty(values.sitesT_4G_FDD) && isEmpty(values.sitesT_4G_TDD)
		&& isEmpty(values.sitesT_5G);

	const isSubmitBtnDisabled: boolean = Object.keys(filteredValuesByKeys)
		.some((key: string) => !!errors[key as keyof IKpiAlertSchema]);

	const maxSelectionText = maxSelection ? `Max ${maxSelection} KPI can be selected` : undefined;

	const modalLabel4GFdd = `${isEdit ? 'View' : 'Select'} sites/cells with \n4G FDD`;
	const modalLabel4GTdd = `${isEdit ? 'View' : 'Select'} sites/cells with \n4G TDD`;
	const modalLabel5G = `${isEdit ? 'View' : 'Select'} sites/cells with ${get5GLabel(isOranVendor)}`;

	return (
		<>
			<div className='checkForContainer'>
				<div className='fieldLabel'>
					<span>Vendor</span>
					<span className='asterisk'>*</span>
				</div>
				<div className='fieldsContainer'>
					<Select
						control={control}
						disabled={isEdit}
						errorMessage={errors?.vendor?.message}
						handleChange={handleChangeVendor}
						isError={!!errors?.vendor?.message}
						name={ECheckForFields.vendor}
						options={vendorsOptions}
						required
						value={vendor}
						width='360px'
					/>
				</div>
				<div className='fieldsContainer flex-column flex-gap_20'>
					<div className='fieldLabel'>
						<span>Technology</span>
						<span className='asterisk'>*</span>
					</div>
					<div className='display-flex flex-column flex-gap_10'>
						{!isOranVendor && (
							<>
								<GroupSelectSitesCells
									checkboxLabel={ETechnologiesLabels.T_4G_FDD}
									defaultOptions={defaultValueT_4G_FDD}
									disableCheckbox={isOranVendor}
									isCheckboxTheme
									isInLine
									label={`${isEdit ? 'View' : 'Select'} sites/cells`}
									modalLabel={modalLabel4GFdd}
									name={ETechnologiesValues.T_4G_FDD}
									onChange={onChangeSitesT_4G_FDD}
									options={options4GFdd}
									readOnly={isEdit}
									searchPlaceholder={SEARCH_BY_SITE_OR_CELL_ID_PLACEHOLDER}
									selectAllName='select-all-4G-FDD-sites'
									withVirtualization
								/>
								<GroupSelectSitesCells
									checkboxLabel={ETechnologiesLabels.T_4G_TDD}
									defaultOptions={defaultValueT_4G_TDD}
									disableCheckbox={isOranVendor}
									isCheckboxTheme
									isInLine
									label={`${isEdit ? 'View' : 'Select'} sites/cells`}
									modalLabel={modalLabel4GTdd}
									name={ETechnologiesValues.T_4G_TDD}
									onChange={onChangeSitesT_4G_TDD}
									options={options4GTdd}
									readOnly={isEdit}
									searchPlaceholder={SEARCH_BY_SITE_OR_CELL_ID_PLACEHOLDER}
									selectAllName='select-all-4G-TDD-sites'
									withVirtualization
								/>
							</>
						)}
						<GroupSelectSitesCells
							checkboxLabel={isOranVendor ? ETechnologiesLabels.T_5G_ORAN : ETechnologiesLabels.T_5G}
							defaultOptions={defaultValueT_5G}
							isCheckboxTheme
							isInLine
							label={`${isEdit ? 'View' : 'Select'} sites/cells`}
							modalLabel={modalLabel5G}
							name={ETechnologiesValues.T_5G}
							onChange={onChangeSitesT_5G}
							options={options5G}
							readOnly={isEdit}
							searchPlaceholder={SEARCH_BY_SITE_OR_CELL_ID_PLACEHOLDER}
							selectAllName='select-all-5G-sites'
							withVirtualization
						/>
						<div className='form-error'>{
							errors[ECheckForFields.sitesT_4G_FDD]?.message
							|| errors[ECheckForFields.sitesT_4G_TDD]?.message
							|| errors[ECheckForFields.sitesT_5G]?.message
						}</div>
					</div>
				</div>
				<div className='fieldsContainer flex-column'>
					<div className='fieldLabel'>
						<span>KPI</span>
						<span className='asterisk'>*</span>
					</div>
					{isOranVendor ? (
						<TreeGroupSelectWrapper
							data={treeKpi}
							initData={kpiGroupData}
							isDisabled={isEdit}
							isDisabledSubmit={isEdit}
							isIgnoreTreeChecked
							isPortal
							maxSelectionNumber={maxSelection}
							maxSelectionText={maxSelectionText}
							onSubmit={handleUpdateTreeKpi}
							selectedKpiNumber={kpi?.length}
							setData={isEdit ? undefined : setTreeKpi}
							setInitData={setInitKpiData}
							setIsDirty={() => null}
							setSelectedKpiNumber={() => null}
							setTreeKpiIds={setTreeKpiIds}
						/>
					) : (
						<GroupSelectKpi
							disabled={isKpiDisabled}
							kpiValues={values.kpi}
							label='Select KPI'
							maxSelectionNumber={maxSelection}
							maxSelectionText={maxSelectionText}
							menuPlacement='top'
							name='KPI'
							onChange={onChangeKpi}
							options={optionsKpi}
							readOnly={isEdit}
							searchPlaceholder='Search by KPI'
						/>
					)}
					<div className='form-error'>{errors[ECheckForFields.kpi]?.message}</div>
				</div>
				<div className='f5gParametersDisplay'>
					<div className='f5gParametersDisplay'>
						<span className='f5gParametersBodyLabel'>Display as:</span>
					</div>
					<div className='f5gParametersDisplayButtons'>
						<div className='f5gParametersDisplayButtonsTitle'>
							<span>Aggregated KPI</span>
						</div>
						<RadioButton
							checked={aggregationType === EAggregationTypes.aggregated}
							disabled={isEdit}
							id={EAggregationTypes.aggregated}
							label='Aggregated KPI'
							name={ECheckForFields.aggregationType}
							register={register}
							value={EAggregationTypes.aggregated}
							withUseForms
						/>
					</div>
					<div className='f5gParametersDisplayButtons'>
						<div className='f5gParametersDisplayButtonsTitle'>
							Site level KPI
						</div>
						<RadioButton
							checked={aggregationType === EAggregationTypes.siteLevel}
							disabled={isEdit}
							id={EAggregationTypes.siteLevel}
							label='Aggregated cells value for a site'
							name={ECheckForFields.aggregationType}
							register={register}
							value={EAggregationTypes.siteLevel}
							withUseForms
						/>
						<RadioButton
							checked={aggregationType === EAggregationTypes.siteLevelAggregatedCells}
							disabled={isEdit}
							id={EAggregationTypes.siteLevelAggregatedCells}
							label='Separate lines for each cell of a site'
							name={ECheckForFields.aggregationType}
							register={register}
							value={EAggregationTypes.siteLevelAggregatedCells}
							withUseForms
						/>
					</div>
				</div>
				<div className='form-error'>{errors[ECheckForFields.aggregationType]?.message}</div>
				{(isLoadingVendors || isLoadingTechnologies) && <SpinnerOverlay />}
			</div>
			<div className='buttonsContainer'>
				<Button
					onClick={onBackHandler}
					text={negativeBtnText}
					withoutBackground
				/>
				<Button
					disabled={isSubmitBtnDisabled}
					onClick={onHandleNext}
					text={positiveBtnText}
				/>
			</div>
		</>
	);
}

export default memo(AddKpiAlertCheckFor);
